<template>
  <div v-if="visible" class="coze-service">
    <!-- 遮罩：小屏点击可收起 -->
    <transition name="coze-service-fade">
      <div v-show="panelOpen" class="coze-service__mask" @click="closePanel" />
    </transition>

    <!-- 悬浮按钮：默认仅圆形机器人；悬停时在左侧显示「AI助手」 -->
    <button
      type="button"
      class="coze-service__fab"
      :class="{ 'coze-service__fab--open': panelOpen }"
      :title="panelOpen ? '收起 AI 助手' : 'AI 助手'"
      :aria-label="panelOpen ? '收起 AI 助手' : '打开 AI 助手'"
      @click="togglePanel"
    >
      <template v-if="!panelOpen">
        <span class="coze-service__fab-bubble" aria-hidden="true">
          <span class="coze-service__fab-text">AI助手</span>
        </span>
        <span class="coze-service__fab-avatar" aria-hidden="true">
          <img class="coze-service__fab-avatar-img" src="/ai-bot-avatar.png" alt="" />
        </span>
      </template>
      <i v-else class="el-icon-arrow-down coze-service__fab-close-icon" />
    </button>

    <!-- 对话窗口 -->
    <transition name="coze-service-panel">
      <div v-show="panelOpen" class="coze-service__panel">
        <header class="coze-service__head">
          <div class="coze-service__head-main">
            <span class="coze-service__avatar">
              <i class="el-icon-service" />
            </span>
            <div>
              <h3>{{ agentName || "AI 客服" }}</h3>
              <p>在线 · 平台知识库 · 经服务端安全调用</p>
            </div>
          </div>
          <div class="coze-service__head-actions">
            <el-button type="text" icon="el-icon-delete" title="清空对话" @click="clearChat" />
            <el-button type="text" icon="el-icon-minus" title="收起" @click="closePanel" />
          </div>
        </header>

        <div ref="msgBox" class="coze-service__msgs">
          <div v-if="!messages.length && !sending" class="coze-service__welcome">
            <i class="el-icon-chat-line-round" />
            <p>你好，我是平台 AI 客服</p>
            <span>可咨询课程、实训、账号使用等问题</span>
          </div>

          <div
            v-for="(m, i) in messages"
            :key="i"
            class="coze-service-msg"
            :class="'coze-service-msg--' + m.role"
          >
            <span v-if="m.role === 'assistant'" class="coze-service-msg__avatar">
              <i class="el-icon-service" />
            </span>
            <div class="coze-service-msg__body">
              <span class="coze-service-msg__role">{{ m.role === "user" ? "我" : agentName || "AI" }}</span>
              <p class="coze-service-msg__text">{{ m.text }}</p>
            </div>
          </div>

          <div v-if="sending" class="coze-service-msg coze-service-msg--assistant">
            <span class="coze-service-msg__avatar">
              <i class="el-icon-service" />
            </span>
            <div class="coze-service-msg__body">
              <span class="coze-service-msg__role">{{ agentName || "AI" }}</span>
              <p class="coze-service-msg__text coze-service-msg__text--typing">
                <span class="coze-service__dot" /><span class="coze-service__dot" /><span class="coze-service__dot" />
              </p>
            </div>
          </div>
        </div>

        <footer class="coze-service__foot">
          <el-input
            v-model="input"
            type="textarea"
            :rows="2"
            resize="none"
            placeholder="请输入问题，Enter 发送，Shift+Enter 换行"
            :disabled="sending"
            @keydown.native="onKeydown"
          />
          <el-button type="primary" :loading="sending" :disabled="!input.trim()" @click="send">发送</el-button>
        </footer>
      </div>
    </transition>
  </div>
</template>

<script>
import { getToken } from "@/utils/auth";
import { faceCozeServiceChat, faceCozeServiceStatus } from "@/api/portal";
import { setCozeServiceEnabled } from "@/utils/cozeService";

const STORAGE_KEY = "coze-service-chat";

/**
 * 全站 AI 客服：悬浮按钮 + 对话窗，仅调用后端 /faceAuth/coze/*（scene=global）
 */
export default {
  name: "CozeServiceBot",
  data() {
    return {
      ready: false,
      enabled: false,
      agentName: "",
      prologue: "",
      panelOpen: false,
      messages: [],
      input: "",
      sending: false,
      conversationId: "",
    };
  },
  computed: {
    visible() {
      if (!this.ready || !this.enabled) return false;
      if (this.$route.meta && this.$route.meta.hideGlobalBot) return false;
      if ((this.$route.path || "").indexOf("/admin") === 0) return false;
      if ((this.$route.path || "").includes("/login")) return false;
      return !!getToken();
    },
  },
  watch: {
    "$route.path"() {
      this.bootstrap();
    },
    visible(v) {
      setCozeServiceEnabled(v);
    },
  },
  mounted() {
    this.restoreSession();
    this.bootstrap();
  },
  beforeDestroy() {
    setCozeServiceEnabled(false);
  },
  methods: {
    restoreSession() {
      try {
        const raw = sessionStorage.getItem(STORAGE_KEY);
        if (!raw) return;
        const saved = JSON.parse(raw);
        this.messages = saved.messages || [];
        this.conversationId = saved.conversationId || "";
      } catch (e) {
        /* ignore */
      }
    },
    persistSession() {
      sessionStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          messages: this.messages,
          conversationId: this.conversationId,
        })
      );
    },
    applyPrologue() {
      if (this.prologue && !this.messages.length) {
        this.messages.push({ role: "assistant", text: this.prologue });
        this.persistSession();
      }
    },
    async bootstrap() {
      if (!getToken()) {
        this.ready = true;
        this.enabled = false;
        setCozeServiceEnabled(false);
        return;
      }
      try {
        const res = await faceCozeServiceStatus();
        const d = (res && res.data) || {};
        this.enabled = !!(d.globalEnabled && d.enabled);
        this.agentName = d.globalAgentName || "AI 客服";
        this.prologue = (d.globalPrologue || "").trim();
        if (this.enabled) {
          this.applyPrologue();
        }
      } catch (e) {
        this.enabled = false;
      } finally {
        this.ready = true;
        setCozeServiceEnabled(this.visible);
      }
    },
    togglePanel() {
      this.panelOpen = !this.panelOpen;
      if (this.panelOpen) {
        this.$nextTick(this.scrollBottom);
      }
    },
    closePanel() {
      this.panelOpen = false;
    },
    clearChat() {
      this.messages = [];
      this.conversationId = "";
      sessionStorage.removeItem(STORAGE_KEY);
      this.applyPrologue();
    },
    onKeydown(e) {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        this.send();
      }
    },
    async send() {
      const text = (this.input || "").trim();
      if (!text || this.sending) return;
      this.messages.push({ role: "user", text });
      this.input = "";
      this.sending = true;
      this.persistSession();
      this.$nextTick(this.scrollBottom);
      try {
        const res = await faceCozeServiceChat({
          message: text,
          conversationId: this.conversationId || undefined,
        });
        const d = (res && res.data) || {};
        const reply = (d.reply || "").trim();
        const cid = d.conversationId;
        if (cid && /^\d{1,19}$/.test(String(cid).trim())) {
          this.conversationId = String(cid).trim();
        }
        this.messages.push({
          role: "assistant",
          text: reply || "暂未收到有效回复，请稍后重试。",
        });
      } catch (e) {
        const msg = (e && e.message && e.message !== "error") ? e.message : "";
        this.messages.push({
          role: "assistant",
          text:
            msg ||
            "请求失败，请确认管理端已配置「全站客服」智能体并完成发布。",
        });
      } finally {
        this.sending = false;
        this.persistSession();
        this.$nextTick(this.scrollBottom);
      }
    },
    scrollBottom() {
      const el = this.$refs.msgBox;
      if (el) el.scrollTop = el.scrollHeight;
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/styles/portal-tokens.scss";

.coze-service {
  position: fixed;
  right: 24px;
  bottom: 24px;
  z-index: 4000;
}

.coze-service__mask {
  display: none;
}

.coze-service__fab {
  display: inline-flex;
  align-items: center;
  gap: 0;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  transition: transform 0.2s ease;

  &:not(.coze-service__fab--open):hover {
    transform: translateY(-2px);
  }

  &--open {
    width: 52px;
    height: 52px;
    justify-content: center;
    border-radius: 50%;
    background: linear-gradient(145deg, #2b7fff 0%, #1d5fd6 100%);
    box-shadow: 0 8px 24px rgba(29, 95, 214, 0.45);
    color: #fff;
  }
}

/* 悬停时在图标左侧显示「AI助手」 */
.coze-service__fab-bubble {
  position: relative;
  display: inline-flex;
  align-items: center;
  height: 40px;
  padding: 0 14px 0 16px;
  margin-right: -6px;
  border-radius: 20px 6px 6px 20px;
  background: linear-gradient(90deg, #2b7fff 0%, #3d8cff 100%);
  box-shadow: 0 6px 20px rgba(43, 127, 255, 0.38);
  color: #fff;
  z-index: 1;
  opacity: 0;
  visibility: hidden;
  transform: translateX(10px);
  pointer-events: none;
  transition: opacity 0.22s ease, transform 0.22s ease, visibility 0.22s ease;

  &::after {
    content: "";
    position: absolute;
    right: -5px;
    top: 50%;
    width: 10px;
    height: 10px;
    margin-top: -5px;
    background: #3588ff;
    transform: rotate(45deg);
    border-radius: 1px;
  }
}

.coze-service__fab:not(.coze-service__fab--open):hover .coze-service__fab-bubble {
  opacity: 1;
  visibility: visible;
  transform: translateX(0);
}

.coze-service__fab-text {
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.06em;
  white-space: nowrap;
}

/* 圆形智能机器人头像 */
.coze-service__fab-avatar {
  position: relative;
  z-index: 2;
  flex-shrink: 0;
  width: 64px;
  height: 64px;
  border-radius: 50%;
  border: 3px solid #fff;
  background: #eef4ff;
  box-shadow: 0 8px 24px rgba(29, 95, 214, 0.35);
  overflow: hidden;
}

.coze-service__fab-avatar-img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center center;
}

.coze-service__fab-close-icon {
  font-size: 22px;
}

.coze-service__panel {
  position: absolute;
  right: 0;
  bottom: 64px;
  width: min(400px, calc(100vw - 48px));
  height: min(560px, calc(100vh - 120px));
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(15, 23, 42, 0.2);
  overflow: hidden;
}

.coze-service__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 12px 14px 16px;
  background: linear-gradient(135deg, $portal-brand 0%, darken($portal-accent, 8%) 100%);
  color: #fff;
}

.coze-service__head-main {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;

  h3 {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  p {
    margin: 2px 0 0;
    font-size: 11px;
    opacity: 0.88;
  }
}

.coze-service__avatar {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.22);
  font-size: 20px;
}

.coze-service__head-actions {
  flex-shrink: 0;

  .el-button {
    color: rgba(255, 255, 255, 0.9);
    padding: 6px;
    font-size: 16px;
    &:hover {
      color: #fff;
    }
  }
}

.coze-service__msgs {
  flex: 1;
  overflow-y: auto;
  padding: 16px 14px;
  background: #f4f7fb;
}

.coze-service__welcome {
  text-align: center;
  padding: 32px 16px;
  color: $portal-muted;

  i {
    font-size: 40px;
    color: rgba($portal-brand, 0.5);
  }
  p {
    margin: 12px 0 6px;
    font-size: 15px;
    font-weight: 600;
    color: $portal-text;
  }
  span {
    font-size: 12px;
    line-height: 1.5;
  }
}

.coze-service-msg {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 14px;

  &--user {
    flex-direction: row-reverse;

    .coze-service-msg__body {
      align-items: flex-end;
    }
    .coze-service-msg__text {
      background: $portal-brand;
      color: #fff;
      border-bottom-right-radius: 4px;
    }
  }
  &--assistant .coze-service-msg__text {
    background: #fff;
    color: $portal-text;
    border-bottom-left-radius: 4px;
    box-shadow: 0 1px 4px rgba(15, 23, 42, 0.06);
  }
}

.coze-service-msg__avatar {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: $portal-brand-light;
  color: $portal-brand;
  font-size: 16px;
}

.coze-service-msg__body {
  display: flex;
  flex-direction: column;
  max-width: calc(100% - 40px);
}

.coze-service-msg__role {
  font-size: 11px;
  color: $portal-muted;
  margin-bottom: 4px;
}

.coze-service-msg__text {
  margin: 0;
  padding: 10px 12px;
  border-radius: 12px;
  font-size: 13px;
  line-height: 1.55;
  white-space: pre-wrap;
  word-break: break-word;

  &--typing {
    display: flex;
    align-items: center;
    gap: 4px;
    min-height: 20px;
    padding: 12px 16px;
  }
}

.coze-service__dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: $portal-muted;
  animation: coze-dot 1.2s infinite ease-in-out;

  &:nth-child(2) {
    animation-delay: 0.15s;
  }
  &:nth-child(3) {
    animation-delay: 0.3s;
  }
}

@keyframes coze-dot {
  0%,
  80%,
  100% {
    opacity: 0.35;
    transform: scale(0.85);
  }
  40% {
    opacity: 1;
    transform: scale(1);
  }
}

.coze-service__foot {
  display: flex;
  gap: 8px;
  align-items: flex-end;
  padding: 12px 14px;
  border-top: 1px solid #eef2f7;
  background: #fff;

  .el-textarea {
    flex: 1;
  }
}

.coze-service-panel-enter-active,
.coze-service-panel-leave-active {
  transition: opacity 0.22s ease, transform 0.22s ease;
}
.coze-service-panel-enter,
.coze-service-panel-leave-to {
  opacity: 0;
  transform: translateY(16px) scale(0.95);
}

.coze-service-fade-enter-active,
.coze-service-fade-leave-active {
  transition: opacity 0.2s ease;
}
.coze-service-fade-enter,
.coze-service-fade-leave-to {
  opacity: 0;
}

@media (max-width: 480px) {
  .coze-service {
    right: 16px;
    bottom: 16px;
  }
  .coze-service__mask {
    display: block;
    position: fixed;
    inset: 0;
    background: rgba(15, 23, 42, 0.35);
    z-index: -1;
  }
  .coze-service__panel {
    width: calc(100vw - 32px);
    height: min(72vh, 560px);
  }
  .coze-service__fab-avatar {
    width: 56px;
    height: 56px;
  }
}
</style>
